import React, { useEffect, useState } from "react";
import { Card, Row, Col, ProgressBar, Button, Spinner, Alert, Table, Badge } from "react-bootstrap";
import { fetchSystemStorage } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";

function SystemStorage() {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const { showToast } = useToast();

  const loadStorageMetrics = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const response = await fetchSystemStorage();
      if (response && response.success && response.data) {
        setStorageData(response.data);
        setLastUpdated(new Date());
        if (isManualRefresh) {
          showToast("Storage metrics refreshed successfully!", "success");
        }
      } else {
        throw new Error(response?.message || "Invalid response format");
      }
    } catch (err) {
      console.error("Error loading system storage:", err);
      setError("Failed to load server storage metrics. Please check server logs.");
      showToast("Failed to fetch storage info", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadStorageMetrics();
  }, []);

  const getProgressVariant = (percentage) => {
    if (percentage >= 90) return "danger";
    if (percentage >= 75) return "warning";
    return "success";
  };

  if (loading) {
    return (
      <div className="subMain-content text-center my-5 py-5">
        <Spinner animation="border" role="status" variant="primary" className="mb-3" />
        <h5 className="text-muted">Scanning system disk and uploads storage...</h5>
      </div>
    );
  }

  const disk = storageData?.disk;
  const uploads = storageData?.uploads;
  const system = storageData?.system;

  return (
    <div className="subMain-content">
      {/* Page Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 mt-2">
        <div>
          <h1 className="h3 fw-bold text-dark mb-1">System & Storage Monitor</h1>
          <p className="text-muted mb-0">
            Real-time server disk capacity and file uploads storage consumption.
          </p>
        </div>
        <div className="d-flex align-items-center gap-3 mt-2 mt-sm-0">
          {lastUpdated && (
            <small className="text-muted">
              Last updated: <strong>{lastUpdated.toLocaleTimeString()}</strong>
            </small>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => loadStorageMetrics(true)}
            disabled={refreshing}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: "500" }}
          >
            {refreshing ? (
              <>
                <Spinner animation="border" size="sm" />
                Refreshing...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                </svg>
                Refresh Metrics
              </>
            )}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      {/* Disk Space Warning Alert if low on space */}
      {disk?.isLowSpace && (
        <Alert variant="danger" className="d-flex align-items-center mb-4 shadow-sm">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" className="bi bi-exclamation-triangle-fill flex-shrink-0 me-3" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div>
            <strong>Warning: Low Server Disk Space!</strong>
            <div>Your server has less than 10% free space remaining ({disk.freeFormatted} available). Please consider cleaning up old files or expanding disk capacity.</div>
          </div>
        </Alert>
      )}

      {/* Main Metric Cards Row */}
      <Row className="g-4 mb-4">
        {/* Card 1: Server Disk Storage */}
        <Col xs={12} lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#3d95b9" viewBox="0 0 16 16">
                  <path d="M4 10a1 1 0 0 1 2 0v2a1 1 0 0 1-2 0v-2zm3 0a1 1 0 0 1 2 0v2a1 1 0 0 1-2 0v-2zm3 0a1 1 0 0 1 2 0v2a1 1 0 0 1-2 0v-2z"/>
                  <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm1 2v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1zm14-1V2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v1h14z"/>
                </svg>
                Primary Server Disk Space
              </h6>
              <Badge bg={disk?.usedPercentage > 90 ? "danger" : disk?.usedPercentage > 75 ? "warning" : "success"}>
                {disk?.usedPercentage}% Used
              </Badge>
            </Card.Header>
            <Card.Body className="p-4 d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex justify-content-between align-items-baseline mb-2">
                  <span className="fs-3 fw-bold text-dark">{disk?.usedFormatted}</span>
                  <span className="text-muted small">of <strong>{disk?.totalFormatted}</strong> Total</span>
                </div>

                <ProgressBar
                  now={disk?.usedPercentage || 0}
                  variant={getProgressVariant(disk?.usedPercentage || 0)}
                  style={{ height: "12px", borderRadius: "6px" }}
                  className="mb-4"
                />

                <Row className="text-center pt-2 border-top">
                  <Col xs={4} className="border-end">
                    <div className="text-muted small mb-1">Total Capacity</div>
                    <div className="fw-bold text-dark">{disk?.totalFormatted}</div>
                  </Col>
                  <Col xs={4} className="border-end">
                    <div className="text-muted small mb-1">Space Used</div>
                    <div className="fw-bold text-danger">{disk?.usedFormatted}</div>
                  </Col>
                  <Col xs={4}>
                    <div className="text-muted small mb-1">Available Free</div>
                    <div className="fw-bold text-success">{disk?.freeFormatted}</div>
                  </Col>
                </Row>
              </div>

              <div className="mt-3 pt-2 text-muted small">
                <span>Free Space Ratio: <strong>{disk?.freePercentage}% free</strong> on root partition.</span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Card 2: Uploads Storage */}
        <Col xs={12} lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#198754" viewBox="0 0 16 16">
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                  <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"/>
                </svg>
                Uploads Directory Consumption
              </h6>
              <Badge bg="info" className="text-white">
                {uploads?.totalFiles} Total Files
              </Badge>
            </Card.Header>
            <Card.Body className="p-4 d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex justify-content-between align-items-baseline mb-2">
                  <span className="fs-3 fw-bold text-primary">{uploads?.totalFormatted}</span>
                  <span className="text-muted small">Path: <code>/{uploads?.folderPath}</code></span>
                </div>

                <p className="text-muted small mb-4">
                  Storage consumed by all submitted documents, application blueprints, and file attachments across all communities.
                </p>

                <Row className="text-center pt-2 border-top">
                  <Col xs={6} className="border-end">
                    <div className="text-muted small mb-1">Total Uploads Size</div>
                    <div className="fw-bold text-primary">{uploads?.totalFormatted}</div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted small mb-1">Total Files Count</div>
                    <div className="fw-bold text-dark">{uploads?.totalFiles} files</div>
                  </Col>
                </Row>
              </div>

              <div className="mt-3 pt-2 text-muted small">
                <span>Active subdirectories: <strong>{uploads?.totalFolders} folders</strong> indexed.</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Row 2: Subfolder Breakdown Table & Server Metadata */}
      <Row className="g-4">
        <Col xs={12} lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold text-dark">Uploads Subfolder Breakdown</h6>
            </Card.Header>
            <Card.Body className="p-0">
              {uploads?.breakdown && uploads.breakdown.length > 0 ? (
                <Table responsive hover className="mb-0 align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">Folder Name</th>
                      <th>Files Count</th>
                      <th>Size Consumed</th>
                      <th className="pe-4 text-end">Proportion of Uploads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.breakdown.map((folder, index) => {
                      const percentage = uploads.totalBytes > 0 
                        ? ((folder.sizeBytes / uploads.totalBytes) * 100).toFixed(1) 
                        : 0;

                      return (
                        <tr key={index}>
                          <td className="ps-4">
                            <div className="d-flex align-items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#ffc107" viewBox="0 0 16 16">
                                <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.826a2 2 0 0 1-1.991-1.819l-.637-7a1.99 1.99 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3zm-8.322.12C1.72 3.042 1.95 3 2.19 3h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981l.006.139z"/>
                              </svg>
                              <span className="fw-semibold text-dark">/{folder.name}</span>
                            </div>
                          </td>
                          <td>
                            <Badge bg="light" className="text-dark border">
                              {folder.fileCount} {folder.fileCount === 1 ? 'file' : 'files'}
                            </Badge>
                          </td>
                          <td className="fw-semibold text-dark">
                            {folder.sizeFormatted}
                          </td>
                          <td className="pe-4 text-end">
                            <div className="d-inline-flex align-items-center gap-2" style={{ minWidth: "120px" }}>
                              <ProgressBar now={percentage} variant="primary" style={{ height: "6px", width: "70px" }} />
                              <span className="small text-muted">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div className="p-4 text-center text-muted">
                  No subdirectories found inside <code>/uploads</code>.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Server System Info Card */}
        <Col xs={12} lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold text-dark">Server Environment Info</h6>
            </Card.Header>
            <Card.Body className="p-3">
              <ul className="list-group list-group-flush small">
                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0">
                  <span className="text-muted">Operating System</span>
                  <span className="fw-semibold text-dark">{system?.platform === 'win32' ? 'Windows Server' : system?.platform || 'Linux'}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0">
                  <span className="text-muted">Node.js Runtime</span>
                  <span className="fw-semibold text-dark">{system?.nodeVersion || 'N/A'}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0">
                  <span className="text-muted">API Server Uptime</span>
                  <span className="fw-semibold text-success">{system?.uptime || 'N/A'}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0">
                  <span className="text-muted">Server Sync Time</span>
                  <span className="fw-semibold text-dark">
                    {system?.serverTime ? new Date(system.serverTime).toLocaleTimeString() : 'N/A'}
                  </span>
                </li>
              </ul>

              <hr className="my-3" />

              <div className="p-3 bg-light rounded text-muted small">
                <div className="fw-bold text-dark mb-1">💡 Storage Best Practice:</div>
                Keep server disk usage below <strong>80%</strong> to ensure optimal performance for PDF generation and large blueprint uploads.
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default SystemStorage;
